import React, { memo } from 'react';
import { Box, Stack, Tooltip, Typography, alpha } from '@mui/material';
import type { TimetableGrid } from '../../../services/timetableService';
import { DAY_SHORT, WEEK, periodsOn, toMinutes } from '../timetableUtils';

/** Each school day as a slim timeline: periods as blocks, breaks as gaps; a short day ends early. */
const SchoolDayPreview = memo<{ grid: TimetableGrid }>(({ grid }) => {
  const rows = grid.rows.filter((r) => toMinutes(r.end) > toMinutes(r.start));
  if (!rows.length || !grid.days.length) {
    return <Typography variant="body2" color="text.secondary">Pick days and add periods to see the week.</Typography>;
  }
  const from = toMinutes(rows[0].start);
  const span = Math.max(1, toMinutes(rows[rows.length - 1].end) - from);
  const pct = (m: number) => `${((m - from) / span) * 100}%`;

  return (
    <Stack spacing={1.25}>
      {WEEK.filter((d) => grid.days.includes(d)).map((day) => {
        const last = periodsOn(grid, day);
        let seen = 0;
        const today = rows.filter((r) => {
          if (r.kind === 'period') { seen += 1; return seen <= last; }
          return seen < last;
        });
        const end = today.length ? today[today.length - 1].end : rows[0].start;
        let n = 0;
        return (
          <Stack key={day} direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="caption" fontWeight={600} sx={{ width: 30, color: 'text.secondary' }}>{DAY_SHORT[day]}</Typography>
            <Box sx={(theme) => ({ position: 'relative', flex: 1, height: 22, borderRadius: '6px', bgcolor: alpha(theme.palette.text.primary, 0.04) })}>
              {today.map((r, i) => {
                const isBreak = r.kind === 'break';
                if (!isBreak) n += 1;
                return (
                  <Tooltip key={i} title={`${r.label || (isBreak ? 'Break' : `Period ${n}`)} · ${r.start}–${r.end}`} disableInteractive>
                    <Box
                      sx={(theme) => ({
                        position: 'absolute', top: isBreak ? 8 : 2, bottom: isBreak ? 8 : 2,
                        left: pct(toMinutes(r.start)), width: `calc(${pct(toMinutes(r.end))} - ${pct(toMinutes(r.start))} - 2px)`,
                        borderRadius: '4px',
                        bgcolor: isBreak ? alpha(theme.palette.text.secondary, 0.25) : alpha(theme.palette.primary.main, 0.55),
                      })}
                    />
                  </Tooltip>
                );
              })}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ width: 40, textAlign: 'right' }}>{end}</Typography>
          </Stack>
        );
      })}
      <Stack direction="row" justifyContent="space-between" sx={{ pl: 5.25, pr: 6.5 }}>
        <Typography variant="caption" color="text.disabled">{rows[0].start}</Typography>
        <Typography variant="caption" color="text.disabled">{rows[rows.length - 1].end}</Typography>
      </Stack>
    </Stack>
  );
});

SchoolDayPreview.displayName = 'SchoolDayPreview';

export default SchoolDayPreview;
